import {createSelector} from 'reselect';
import memoize from 'lodash/memoize';

import {
  IAddBlockResultAction,
  IExecutedBlockAction,
  ILoadedBlockResultsAction,
  IUnsetBlockExecution,
  IUpdateBlockResultAction,
  BlockResultActions,
  BlockResultActionsTypes,
} from '../actions/blockResultActions';
import {
  ILoadedNoteflowTaskResultsAction,
  NoteflowActionTypes,
} from '../actions/noteflowActions';
import { ApiStatus } from '../../models';
import { deleteObjProperty } from '../../utils/reducers';
import { IState } from "./index";

type IResult = {
  block_uid: string;
  [key: string]: any;
}

type ExecutionStatus = {
  [key: string]: ApiStatus
};

export const initialBlockResultState: IBlockResultState = {
  results: null,
  executionStatus: null,
};

export interface IBlockResultState {
  results: IResult[] | null;
  executionStatus: ExecutionStatus | null;
}

export default function blockResultReducer(
  state: IBlockResultState = initialBlockResultState,
  action:
    BlockResultActions |
    ILoadedNoteflowTaskResultsAction
): IBlockResultState {
  switch (action.type) {
    case NoteflowActionTypes.LOADED_NOTEFLOW_TASK_RESULTS:
      return handleNoteflowTaskResults(state, action);
    case BlockResultActionsTypes.LOADED_BLOCK_RESULTS:
      return handledLoadedBlockResults(state, action);
    case BlockResultActionsTypes.EXECUTE_BLOCK:
      return {
        ...state,
        executionStatus: handleExecutionStatus(state.executionStatus, action.payload.uid, ApiStatus.LOADING)
      }
    case BlockResultActionsTypes.EXECUTING_BLOCK_FAILED:
      return {
        ...state,
        executionStatus: handleExecutionStatus(state.executionStatus, action.payload.uid, ApiStatus.FAILED),
        results: handleSetBlockResultStatusFailed(state, action.payload.resultId),
      }
    case BlockResultActionsTypes.UNSET_BLOCK_EXECUTION:
      return handleUnsetExecutionStatus(state, action);
    case BlockResultActionsTypes.ADD_BLOCK_RESULT:
      return handleAddBlockResult(state, action);
    case BlockResultActionsTypes.EXECUTED_BLOCK:
      return {
        ...handleUpdateBlockResult(state, action),
        executionStatus: handleExecutionStatus(state.executionStatus, action.payload.uid, ApiStatus.LOADED)
      }
    case BlockResultActionsTypes.UPDATE_BLOCK_RESULT:
      return handleUpdateBlockResult(state, action);
    case BlockResultActionsTypes.UNSET_BLOCK_EXECUTIONS:
      return {
        ...state,
        results: null,
        executionStatus: null,
      }
    default:
      return state;
  }
}

// Functions

function toResult(result: any, block_uid: string): any {

  return {
    block_uid,
    ...result,
  };
}

function createEmptyResult(result_id: string, block_uid: string): any {
  return {
    block_uid,
    key: result_id,
    status: 'pending',
    start_time: new Date().getTime(),
  }
}

function handleNoteflowTaskResults(
  state: IBlockResultState,
  action: ILoadedNoteflowTaskResultsAction
): IBlockResultState {
  const newPayload = action.payload?.reduce((acc: any, item: any) => {
    acc[item.block_uid] = item.uid;

    return acc;
  }, {}); // { block_uid: result_id, ... }

  const newAction: any = {
    payload: newPayload
  };

  return handleExecutedBlocks(state, newAction);
}

function handleExecutedBlocks(
  state: IBlockResultState,
  action: any
): IBlockResultState {
  /*
   * action.payload represents an object with { block_uid: result_id, ... }
   */
  const parsePayload = Object.keys(action.payload)
    .filter(blockUid => action.payload[blockUid]) || [];

  const emptyResults = parsePayload.map(blockUid => createEmptyResult(action.payload[blockUid], blockUid));

  const newResults = state.results ?
    [ ...emptyResults, ...state.results ] : [ ...emptyResults ];

  const newExecutionStatus = parsePayload.reduce((acc: any, blockUid: string) => {
    acc[blockUid] = ApiStatus.LOADING;

    return acc;
  }, {});

  return {
    ...state,
    results: newResults,
    executionStatus: state.executionStatus ? {
      ...state.executionStatus,
      ...newExecutionStatus
    } : newExecutionStatus
  }
}

function handledLoadedBlockResults(
  state: IBlockResultState,
  action: ILoadedBlockResultsAction,
): IBlockResultState {
  const { uid, result } = action.payload;
  const convertedResults = result.map((r: any) => toResult(r, uid));

  const newResults = state.results ?
    [ ...convertedResults, ...state.results ] :
    [ ...convertedResults ];

  return {
    ...state,
    results: newResults,
  };
}

function handleAddBlockResult(
  state: IBlockResultState,
  action: IAddBlockResultAction,
): IBlockResultState {
  const { uid, result } = action.payload;
  const newResult = toResult(result, uid);

  const newResults = state.results ?
    [ newResult, ...state.results ] :
    [ newResult ];

  return {
    ...state,
    results: newResults,
  };
}

function handleUpdateBlockResult(
  state: IBlockResultState,
  action: IUpdateBlockResultAction | IExecutedBlockAction,
): IBlockResultState {
  const LAST_RESULT_KEY: string = 'last_result';
  let { uid, resultId, result } = action.payload;

  if (result === null) {
    // something went wrong, remove result
    return {
      ...state,
      results: state.results ? state.results.filter(r => r.key !== resultId) : null,
    }
  }

  let last_results: any[] = [];
  if (resultId == LAST_RESULT_KEY) {
    /*
     * Remove `last_results` key from result if it exists
     */
    let {last_results: _last_results, ...rest} = result;
    last_results = _last_results || [];
    result = rest;
  }

  const newResult = toResult(result, uid);
  /*
   * Old queries have only `key`, which represents their `uid`;
   * New queries are run inside "noteflows" and they are tied to a `noteflow_run_id`;
   * - the `noteflow_run_id` identifier is sent to the FE (because the Query is not created yet)
   * - the FE pulls for `noteflow_run_id` until the query finishes execution
   * - after the Query executed, we check for which `results` element to update based on `key`
   *   (`key` becomes `query.uid`)
   */
  const newResultIdentifier = newResult.noteflow_run_uid || newResult.key;

  let newResults = state.results ?
    state.results.map(result => result.key === newResultIdentifier ? newResult : result) :
    [ newResult ];

  if (last_results.length) {
    /*
     * Add last results for this block, if exists.
     * Add Status to `original_status` and replace original status with a fake `pending` one
     *   in order to trigger `loading` on the FE.
     */
    newResults = newResults
      .concat(last_results.map((lastResult: any) => {
        return {
          ...toResult(lastResult, uid),
          status: 'pending',
          original_status: lastResult.status,
        }
      }));
  }

  return {
    ...state,
    results: newResults,
  };
}

function handleSetBlockResultStatusFailed(
  state: IBlockResultState,
  resultId: string | undefined | null,
): IResult[] | null {
  /*
   * We update the status to failed (usually) in order to stop fetching for it
   * (when it failed on the BE, but it remained in running state on the FE)
   */
  if (!resultId) {
    /*
     * if no resultId, don't do anything
     */
    return state.results;
  }
  // status of a query can be: 'pending', 'running', 'failed', 'success'
  const FAILED_STATUS = "failed";

  return state.results?.map(
    (result: any) => result.key === resultId ? {
      ...result,
      status: FAILED_STATUS,
    } : result
  ) || [];
}

function handleExecutionStatus(executionStatus: ExecutionStatus | null, uid: string, status: ApiStatus) {
  const newStatus = { [uid]: status };

  return executionStatus ? { ...executionStatus, ...newStatus } : newStatus;
}

function handleUnsetExecutionStatus(state: IBlockResultState, action: IUnsetBlockExecution) {
  let newExecutionStatus: any = deleteObjProperty(state.executionStatus, action.payload);

  if (newExecutionStatus && !Object.keys(newExecutionStatus).length) {
    newExecutionStatus = null;
  }

  return {
    ...state,
    executionStatus: newExecutionStatus,
  }
}

// Selectors

export const blockResultsSelector = memoize((blockUid: string | null | undefined) => createSelector(
  (state: IState) => state.blockResult.results,
  (results: IResult[] | null): any[] | null => {
    return results?.filter(
      (result: IResult) => result?.block_uid === blockUid) || null;
  },
));

const blockResultHashFn = (
  blockUid: string | null | undefined,
  resultId: string | null | undefined
) => `${blockUid || '-'}_${resultId || '-'}`;

export const blockResultSelector = memoize((
  blockUid: string | null | undefined,
  resultId: string | null | undefined,
) => createSelector(
  (state: IState) => state.blockResult.results,
  (results: IResult[] | null): any | null => {

    if (resultId) {
      /*
       * If `resultId` exists, fetch specific result
       */
      return results?.find(
        (result: IResult) => result?.key === resultId || result?.task_run_id === resultId) || null;
    }
    /*
     * If no `resultId` is present, default to last result
     * results are appended to the beginning of the list, so first result is the last added
     */
    return results?.find(
      (result: IResult) => result?.block_uid === blockUid) || null;
  },
), blockResultHashFn);

export const blockLastResultSelector = memoize((blockUid: string | null | undefined) => createSelector(
  (state: IState) => state.blockResult.results,
  (results: IResult[] | null): any | null => {
    /*
     * results are appended to the beginning of the list, so first result is the last added
     */
    return results?.find(
      (result: IResult) => result?.block_uid === blockUid) || null;
  },
));

export const blocksLastResultsSelector = memoize((blockUids: string[] | null | undefined) => createSelector(
  (state: IState) => state.blockResult.results,
  (results: IResult[] | null): any | null => {
    /*
     * results are appended to the beginning of the list, so first result is the last added
     * only results with status 'success' are used here
     */
    return blockUids?.map(
      blockUid => results
        ?.filter(result => result.status === 'success')
        .find(
          (result: IResult) => result?.block_uid === blockUid
        ) || null
    )?.filter(result => !!result);
  },
));

export const getBlockExecutionStatusSelector = memoize((blockUid: string | null | undefined) => createSelector(
  (state: IState) => state.blockResult.executionStatus,
  (executionStatus: ExecutionStatus | null): ApiStatus | null => {
    return blockUid ? (executionStatus?.[blockUid] || null) : null;
  },
));
